import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type PinchZoomOptions = {
  min: number;
  max: number;
  step?: number;
  initial?: number;
};

type PinchZoom<T extends HTMLElement> = {
  /** committed zoom — safe to use for expensive re-renders (a PDF canvas) */
  scale: number;
  /** live multiplier on top of `scale` while pinching, 1.0 otherwise — meant for a CSS transform */
  previewScale: number;
  /** what the user currently sees: `scale * previewScale` */
  liveScale: number;
  zoomIn: () => void;
  zoomOut: () => void;
  pinchRef: React.RefCallback<T>;
};

const WHEEL_COMMIT_DELAY = 200;

/** the point the gesture started on, so zooming can keep it under the fingers */
type ZoomAnchor = {
  /** offset inside the scroll container */
  x: number;
  y: number;
  /** the same point in scrollable content coordinates */
  contentX: number;
  contentY: number;
  /** scale the content had when the gesture started */
  base: number;
};

const getTouchDistance = (touches: TouchList) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

/**
 * Zoom state shared by the preview viewers: zoom buttons plus a two-finger
 * pinch (and trackpad pinch, which browsers report as ctrl + wheel) on the
 * element `pinchRef` is attached to.
 *
 * A pinch only moves `previewScale` and commits into `scale` when the fingers
 * lift, so viewers can stay cheap during the gesture — re-rendering a PDF page
 * on every touch event replaces its canvas, which cancels the gesture midway.
 */
export const usePinchZoom = <T extends HTMLElement>({
  min,
  max,
  step = 0.2,
  initial = 1.0,
}: PinchZoomOptions): PinchZoom<T> => {
  const [scale, setScale] = useState(initial);
  const [previewScale, setPreviewScale] = useState(1);
  // a callback ref, so listeners bind even when the target mounts later (e.g. after a doc loads).
  // kept in a ref too, since scroll is adjusted on it and state values must not be mutated
  const [element, setElement] = useState<T | null>(null);
  const elementRef = useRef<T | null>(null);
  const pinchRef = useCallback((node: T | null) => {
    elementRef.current = node;
    setElement(node);
  }, []);
  const scaleRef = useRef(scale);
  const anchorRef = useRef<ZoomAnchor | null>(null);
  const pinchingRef = useRef(false);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const clamp = useCallback((value: number) => Math.min(Math.max(value, min), max), [max, min]);

  const zoomIn = useCallback(() => setScale((prev) => clamp(prev + step)), [clamp, step]);
  const zoomOut = useCallback(() => setScale((prev) => clamp(prev - step)), [clamp, step]);

  useEffect(() => {
    if (!element) return;

    let startDistance = 0;
    let preview = 1;
    let frame: number | null = null;
    let wheelIdle: ReturnType<typeof setTimeout> | null = null;

    // one update per frame — a pinch fires far more often than viewers can repaint
    const schedule = (update: () => void) => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };

    // rounded only enough to keep float noise out — a coarser step would resize the
    // content the moment the fingers lift, which reads as the page jumping
    const commit = (next: number) => {
      preview = 1;
      setPreviewScale(1);
      setScale(Math.round(clamp(next) * 10000) / 10000);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      startDistance = getTouchDistance(event.touches);
      preview = 1;
      pinchingRef.current = true;

      const rect = element.getBoundingClientRect();
      const x = (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
      const y = (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;
      anchorRef.current = {
        x,
        y,
        contentX: element.scrollLeft + x,
        contentY: element.scrollTop + y,
        base: scaleRef.current,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !startDistance) return;
      event.preventDefault();
      const ratio = getTouchDistance(event.touches) / startDistance;
      // clamp through the committed scale so the preview can't exceed the zoom limits
      preview = clamp(scaleRef.current * ratio) / scaleRef.current;
      schedule(() => setPreviewScale(preview));
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2 || !startDistance) return;
      startDistance = 0;
      pinchingRef.current = false;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      commit(scaleRef.current * preview);
    };

    // trackpad pinch — no gesture-end event, so commit once the wheel goes quiet
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      preview = clamp(scaleRef.current * preview * (1 - event.deltaY / 100)) / scaleRef.current;
      schedule(() => setPreviewScale(preview));
      if (wheelIdle !== null) clearTimeout(wheelIdle);
      wheelIdle = setTimeout(() => {
        wheelIdle = null;
        commit(scaleRef.current * preview);
      }, WHEEL_COMMIT_DELAY);
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);
    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      if (wheelIdle !== null) clearTimeout(wheelIdle);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
      element.removeEventListener('wheel', handleWheel);
    };
  }, [clamp, element]);

  // the content grows from its top-left corner, so scroll has to follow for the pinched
  // point to stay put — runs before paint, and once more after the gesture commits
  useLayoutEffect(() => {
    const container = elementRef.current;
    const anchor = anchorRef.current;
    if (!container || !anchor) return;

    const factor = (scale * previewScale) / anchor.base;
    container.scrollLeft = anchor.contentX * factor - anchor.x;
    container.scrollTop = anchor.contentY * factor - anchor.y;

    if (!pinchingRef.current) anchorRef.current = null;
  }, [element, previewScale, scale]);

  return { scale, previewScale, liveScale: scale * previewScale, zoomIn, zoomOut, pinchRef };
};
